import { CallApi, handleJSONResponse } from './common';
import { PaymentIntent } from '@stripe/stripe-js';
import { Stripe } from 'stripe';
import { CreateCheckoutSessionRequest } from '@services/payment/types/api/create-checkout-session-req';
import { CreateRecurringPaymentRequest } from '@services/payment/types/api/create-recurring-payment-req';
import { UpdateRecurringPaymentRequest } from '@services/payment/types/api/update-recurring-payment-req';
import { CreatePaymentIntentRequest } from '@services/payment/types/api/create-payment-intent-req';
import { CustomerMetadata } from '@services/payment/types/api/customer-metadata';
import { RetryRecurringPaymentRequest } from '@services/payment/types/api/retry-recurring-payment-req';
import { ListPaymentMethodsResponse } from '@services/payment/types/api/list-payment-methods-response';
import { ChangePaymentMethodRequest } from '@services/payment/types/api/change-payment-method-request';
import { ChangePaymentMethodResponse } from '@services/payment/types/api/change-payment-method-response';

const callAPI = CallApi('payment');

export const createPaymentIntent = async (
  payload: CreatePaymentIntentRequest
): Promise<PaymentIntent> => {
  const response = await callAPI(`/intent`, 'POST', payload);

  return handleJSONResponse(response);
};

interface SubmitStripeOrder {
  orderId: string;
  totalCost: number;
}

export const submitStripeOrder = async ({
  totalCost,
  orderId,
}: SubmitStripeOrder): Promise<PaymentIntent> => {
  const payload: SubmitStripeOrder = {
    totalCost,
    orderId,
  };

  const response = await callAPI(
    `/submit-stripe-order-customer`,
    'POST',
    payload
  );

  return handleJSONResponse(response);
};

export const createRecurringPayment = async (
  ownerId: string,
  subscriptionId: string,
  paymentMethodId: string,
  priceId: string,
  customer: CustomerMetadata,
  couponId?: string,
  trialDays?: number
): Promise<any> => {
  const priceIds: string[] = [priceId];

  const payload: CreateRecurringPaymentRequest = {
    ownerId,
    subscriptionId,
    paymentMethodId,
    priceIds,
    couponId,
    customer,
    trialDays,
  };

  const response = await callAPI(`/subscription`, 'POST', payload);

  return handleJSONResponse(response);
};

export const updateRecurringPayment = async (
  ownerId: string,
  paymentMethodId: string,
  subscriptionId: string,
  externalSubscriptionId: string,
  priceId: string,
  customer: CustomerMetadata,
  couponId?: string
): Promise<any> => {
  const priceIds: string[] = [priceId];

  const payload: UpdateRecurringPaymentRequest = {
    ownerId,
    subscriptionId,
    externalSubscriptionId,
    paymentMethodId,
    priceIds,
    couponId,
    customer,
  };

  const response = await callAPI(`/subscription`, 'PUT', payload);

  return handleJSONResponse(response);
};

export const retry = async (
  customerId: string,
  paymentMethodId: string,
  invoiceId: string,
  customer: CustomerMetadata
) => {
  const payload: RetryRecurringPaymentRequest = {
    customerId,
    paymentMethodId,
    invoiceId,
    customer,
  };

  const response = await callAPI(`/subscription`, 'PATCH', payload);

  return handleJSONResponse(response);
};

export const invoices = async (customerId: string) => {
  const response = await callAPI(`/invoices/${customerId}`, 'GET');

  return handleJSONResponse(response);
};

export const listPaymentMethods = async (
  customerId: string
): Promise<ListPaymentMethodsResponse> => {
  const response = await callAPI(`/methods/${customerId}`, 'GET');

  return handleJSONResponse(response);
};

export const addPaymentMethod = async (
  customerId: string,
  paymentMethodId: string
): Promise<ChangePaymentMethodResponse> => {
  const payload: ChangePaymentMethodRequest = { paymentMethodId };

  const response = await callAPI(`/methods/${customerId}`, 'POST', payload);

  return handleJSONResponse(response);
};

export const removePaymentMethod = async (
  customerId: string,
  paymentMethodId: string
): Promise<ChangePaymentMethodResponse> => {
  const payload: ChangePaymentMethodRequest = { paymentMethodId };

  const response = await callAPI(`/methods/${customerId}`, 'DELETE', payload);

  return handleJSONResponse(response);
};

export const createStripeCheckoutSession = async (
  request: Partial<CreateCheckoutSessionRequest>
): Promise<Stripe.Checkout.Session> => {
  const response = await callAPI('/session', 'POST', request);

  return handleJSONResponse(response);
};

export const getStripeCheckoutSession = async (
  sessionId: string
): Promise<Stripe.Checkout.Session> => {
  const response = await callAPI(`/payment/session/${sessionId}`, 'GET');

  return handleJSONResponse(response);
};
